import { describeStep, formatDuration } from './timeline';
import type { FlowDefinition } from '../types/api';

type Step = FlowDefinition['steps'][number];

/** The reserved branch target of core (`END_TARGET`): the run completes. */
const END_TARGET = 'end';

/** One way out of a branching step, as the canvas draws it. */
export interface FlowBranch {
  /** Chip text: "Yes", "No", "A · 50%" (several paths to one target are joined). */
  label: string;
  /** `"<abStepId>:<pathId>"` keys of the per-path numbers, one per path that leads here. */
  pathKeys: string[];
  /** The steps of this branch, top to bottom. Empty when the branch goes straight to the join. */
  items: FlowItem[];
  /** Index of the step the branch jumps to when that step is not drawn inside it (the join, a step past it, or the end). */
  to: number;
}

export type FlowItem =
  | { kind: 'step'; step: Step; index: number }
  | {
      kind: 'fork';
      step: Step;
      index: number;
      branches: FlowBranch[];
    };

export interface FlowLayout {
  items: FlowItem[];
  /** Steps no path reaches (a branch skipped them). They are still listed so nothing is hidden. */
  orphans: { step: Step; index: number }[];
}

interface Edge {
  to: number;
  label: string;
  pathKey?: string;
}

/** Edges out of a step. Plain steps go to the next one; `end` goes to the finish; only `split` and `ab_split` fan out. */
function edgesOf(steps: Step[], index: number): Edge[] {
  const n = steps.length;
  const step = steps[index] as Step;
  const indexOf = (target: unknown): number => {
    if (typeof target !== 'string' || target === END_TARGET) return n;
    const found = steps.findIndex((s) => s.id === target);
    // An unknown or backwards target is drawn as going to the end rather than looping.
    return found > index ? found : n;
  };
  if (step.type === 'end') return [{ to: n, label: '' }];
  if (step.type === 'split') {
    return [
      { to: indexOf(step.yesTo), label: 'Yes' },
      { to: indexOf(step.noTo), label: 'No' },
    ];
  }
  if (step.type === 'ab_split' && Array.isArray(step.paths)) {
    return (step.paths as { id: string; label?: string; percent?: number; to?: string }[]).map(
      (p) => ({
        to: indexOf(p.to),
        label: `${p.label || p.id}${typeof p.percent === 'number' ? ` · ${p.percent}%` : ''}`,
        pathKey: `${step.id}:${p.id}`,
      }),
    );
  }
  return [{ to: index + 1, label: '' }];
}

/**
 * Lays a flow out as a tree: a column of steps, where a `split` or `ab_split`
 * opens side-by-side branches that are joined again at the first step all of
 * them reach. Branch targets always point forwards (core validates that), so
 * the walk terminates; every step is drawn once. Index `steps.length` stands
 * for "the flow ends".
 */
export function layoutFlow(definition: FlowDefinition): FlowLayout {
  const steps = definition.steps;
  const n = steps.length;
  const edges = steps.map((_, i) => edgesOf(steps, i));

  // reach[i]: every index reachable from i (i included), the finish n included.
  const reach: Set<number>[] = new Array<Set<number>>(n + 1);
  reach[n] = new Set([n]);
  for (let i = n - 1; i >= 0; i--) {
    const set = new Set<number>([i]);
    for (const e of edges[i] as Edge[]) for (const r of reach[e.to] as Set<number>) set.add(r);
    reach[i] = set;
  }

  const drawn = new Set<number>();

  function walk(start: number, stop: number): FlowItem[] {
    const out: FlowItem[] = [];
    let i = start;
    while (i < stop && i < n && !drawn.has(i)) {
      drawn.add(i);
      const step = steps[i] as Step;
      const es = edges[i] as Edge[];
      const targets = [...new Set(es.map((e) => e.to))];
      if (targets.length <= 1) {
        out.push({ kind: 'step', step, index: i });
        const next = targets[0] ?? i + 1;
        if (step.type === 'end') break;
        i = next;
        continue;
      }
      // Join: the first step every branch reaches (at worst the finish).
      let join = n;
      const common = [...(reach[targets[0] as number] as Set<number>)]
        .filter((r) => targets.every((t) => (reach[t] as Set<number>).has(r)))
        .sort((a, b) => a - b);
      if (common.length > 0) join = common[0] as number;
      const branchStop = Math.min(join, stop);
      const branches: FlowBranch[] = targets.map((t) => {
        const own = es.filter((e) => e.to === t);
        return {
          label: own.map((e) => e.label).join(' / '),
          pathKeys: own.flatMap((e) => (e.pathKey ? [e.pathKey] : [])),
          items: t < branchStop ? walk(t, branchStop) : [],
          to: t,
        };
      });
      out.push({ kind: 'fork', step, index: i, branches });
      i = join;
    }
    return out;
  }

  const items = walk(0, n);
  const orphans = steps.flatMap((step, index) => (drawn.has(index) ? [] : [{ step, index }]));
  return { items, orphans };
}

/** Visits every step of a layout once, in drawing order (used by tests and the count line). */
export function flattenLayout(layout: FlowLayout): number[] {
  const out: number[] = [];
  const visit = (items: FlowItem[]) => {
    for (const item of items) {
      out.push(item.index);
      if (item.kind === 'fork') for (const b of item.branches) visit(b.items);
    }
  };
  visit(layout.items);
  for (const o of layout.orphans) out.push(o.index);
  return out;
}

/** A short name for what a step is, shown as the node heading. */
export const NODE_TITLES: Record<string, string> = {
  wait: 'Wait',
  condition: 'Condition',
  create_coupon: 'Discount code',
  send_template: 'Send email',
  update_contact: 'Update contact',
  split: 'Split',
  ab_split: 'A/B split',
  end: 'End',
  internal_alert: 'Team alert',
  webhook: 'Webhook',
};

function hostOf(url: unknown): string {
  if (typeof url !== 'string') return '';
  try {
    return new URL(url).host;
  } catch {
    return '';
  }
}

function splitCondition(step: Step): string {
  const c = step.condition as { kind?: string; event?: string } | undefined;
  switch (c?.kind) {
    case 'filter':
      return 'Contact matches a filter';
    case 'segment':
      return 'Contact is in a saved segment';
    case 'ordered_since_entry':
      return 'Contact ordered after entering';
    case 'engaged_previous_mail':
      return `Contact ${c.event === 'clicked' ? 'clicked' : 'opened'} the last mail`;
    default:
      return 'Condition';
  }
}

/** One line for a node: delay length, mail subject and languages, condition, split percentages. */
export function summarizeStep(step: Step): string {
  switch (step.type) {
    case 'wait':
      return typeof step.seconds === 'number' ? formatDuration(step.seconds) : 'Unknown duration';
    case 'send_template': {
      const subject = typeof step.subject === 'string' ? step.subject : '';
      const variants = Array.isArray(step.variants) ? step.variants.length : 0;
      const langs = variants > 0 ? ` · ${variants + 1} languages` : '';
      return `"${subject}"${langs}`;
    }
    case 'split':
      return splitCondition(step);
    case 'ab_split': {
      const paths = Array.isArray(step.paths) ? (step.paths as { percent?: number }[]) : [];
      return paths.map((p) => `${p.percent ?? '?'}%`).join(' / ');
    }
    case 'end':
      return 'The flow stops here';
    case 'internal_alert':
      return typeof step.subject === 'string' ? `"${step.subject}"` : 'Alert to the team';
    case 'webhook':
      return hostOf(step.url) || 'Calls a web address';
    case 'update_contact': {
      const action = step.action;
      if (action === 'set_field') return `Set ${String(step.key ?? '?')}`;
      if (action === 'add_label') return `Add tag ${String(step.label ?? '?')}`;
      if (action === 'remove_label') return `Remove tag ${String(step.label ?? '?')}`;
      return 'Stop sending to the contact (inactive)';
    }
    default:
      return describeStep(step);
  }
}
